import { useCallback, useState } from 'react';
import {
  SelectionAdapter,
  SelectionAdapterHook,
} from 'src/components/DataTable/types/DataTableSelection';
import { useSendWelcomeEmailToDiners } from 'src/common/features/DinersManagement/hooks/useSendWelcomeEmailToDiners';
import {
  IErrorResponse,
  SdkErrorResponse,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';
import { getSingularOrPlural } from 'src/utilities/getSingularOrPlural';

export interface EmployeeListSelectionAdapterConfig {
  corpId: string;
  onSent: (dinerId: string) => void;
}

function handleError(error: IErrorResponse | SdkErrorResponse): void {
  pushErrorFromAPI(error, {
    fallbackMessage: 'Your welcome email was not sent. Try again.',
  });
}

export function createEmployeeListSelectionAdapter({
  corpId,
  onSent,
}: EmployeeListSelectionAdapterConfig): SelectionAdapterHook {
  return function useEmployeeListSelectionAdapterHook(): SelectionAdapter {
    const [initialSelections, setInitialSelections] = useState<string[]>([]);

    const clearSelections = useCallback((): void => {
      setInitialSelections([]);
    }, [setInitialSelections]);

    const handleSuccess = useCallback(
      (dinerIds: string[]): void => {
        pushSuccess(
          `Your welcome email was sent to ${
            dinerIds.length
          } ${getSingularOrPlural(dinerIds.length, 'employee')}`
        );
        dinerIds.forEach((dinerId) => onSent(dinerId));
        clearSelections();
      },
      [clearSelections]
    );

    const { sendEmail, isLoading } = useSendWelcomeEmailToDiners({
      corpId,
      onSuccess: handleSuccess,
      onError: handleError,
    });

    const save = useCallback(
      async (selections: string[]): Promise<void> => {
        await sendEmail(selections);
      },
      [sendEmail]
    );

    return {
      initialSelections,
      isLoading: Boolean(isLoading),
      save,
    };
  };
}
